import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssistantConversation } from '@xtreed/academy-shared';
import i18n from '@/i18n';
import { notifyApiError } from '@/lib/api-message';
import { assistantService } from '@/services/assistant.service';
import { useAssistantStore } from '@/stores/assistant';
import { assistantKeys } from './assistantKeys';
import type { MessagesCache } from './useAssistantMessages';

export function useAssistantConversations(enabled = true) {
  return useQuery({
    queryKey: assistantKeys.conversations(),
    queryFn: assistantService.listConversations,
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateAssistantConversation() {
  const queryClient = useQueryClient();
  const setActive = useAssistantStore((state) => state.setActiveConversation);

  return useMutation({
    mutationFn: (title?: string) => assistantService.createConversation(title),
    onSuccess: (conversation) => {
      queryClient.setQueryData<AssistantConversation[]>(
        assistantKeys.conversations(),
        (current) => [conversation, ...(current ?? [])],
      );
      queryClient.setQueryData<MessagesCache>(
        assistantKeys.messages(conversation.id),
        { pages: [{ items: [], nextCursor: null }], pageParams: [null] },
      );
      setActive(conversation.id);
      queryClient.invalidateQueries({
        queryKey: assistantKeys.conversations(),
      });
    },
    onError: (error) =>
      notifyApiError(error, i18n.t('adminAssistant:errors.createConversation')),
  });
}

export function useDeleteAssistantConversation() {
  const queryClient = useQueryClient();
  const { activeConversationId, setActiveConversation } = useAssistantStore();

  return useMutation({
    mutationFn: (id: string) => assistantService.deleteConversation(id),
    onSuccess: (_result, id) => {
      if (activeConversationId === id) setActiveConversation(null);
      queryClient.removeQueries({ queryKey: assistantKeys.messages(id) });
      queryClient.invalidateQueries({
        queryKey: assistantKeys.conversations(),
      });
    },
    onError: (error) =>
      notifyApiError(error, i18n.t('adminAssistant:errors.deleteConversation')),
  });
}
